import { gql } from '@apollo/client';
import { HighlightFragment, useReadMoreQuery } from '../generated/graphql';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouter } from 'next/router';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box } from '@rebass/grid';
import Highlights from './Highlights';
import { searchHitToHighlight } from '../utils/fragmentConverters';
import removeTrailingSlash from 'remove-trailing-slash';

export const query = gql`
    query ReadMore($parentId: Int, $id: Int, $q: String, $notId: [QueryArgument], $lang: String) {
        siblings: entries(descendantOf: $parentId, descendantDist: 1, limit: 4, type: ["page", "article"], id: $notId) {
            ...Highlight
        }

        ancestors: entries(ancestorOf: $id, limit: 4, type: ["page", "article"]) {
            ...Highlight
        }

        search(q: $q, language: $lang, type: ["pages", "articles"]) {
            ...SearchHit
        }
    }
`;

interface Props {
    parentId: Maybe<number>;
    id: Maybe<number>;
    searchTags: string[];
    ignoredUris?: string[];
}

const ReadMore: FC<React.PropsWithChildren<Props>> = ({ id, parentId, searchTags, ignoredUris = [] }) => {
    const { query: params, asPath } = useRouter();
    const { data, loading } = useReadMoreQuery({
        ssr: false,
        variables: {
            id,
            notId: `!= ${id}`,
            parentId,
            q: searchTags.join(' '),
            lang: params.locale as string,
        },
    });
    const items = useMemo(() => {
        const hits = searchHitToHighlight(data?.search).filter(highlight => highlight.uri !== asPath);

        const potentialCards = [
            ...hits,
            ...(data?.siblings?.map(sibling => ({ ...sibling, uri: `/${sibling?.uri}` })) ?? []),
            ...(data?.ancestors?.map(ancestor => ({ ...ancestor, uri: `/${ancestor?.uri}` })) ?? []),
        ];

        const cards = (potentialCards ?? [])
            .filter(
                (card): card is HighlightFragment =>
                    // we don't want to show our current page
                    !!card.uri && removeTrailingSlash(card.uri) !== removeTrailingSlash(asPath)
            )
            .filter(card => {
                const uriWithoutTrailingSlash = card.uri?.replace(/\/$/, '') ?? '';
                return uriWithoutTrailingSlash && !ignoredUris.includes(uriWithoutTrailingSlash);
            });
        // cap at 4
        cards.splice(4);
        return cards;
    }, [asPath, data, ignoredUris]);

    return (
        <Box pb={8} mb={8}>
            <Title variant={'regular'}>
                <FormattedMessage defaultMessage="Lees ook" />
            </Title>
            <Box pt={5}>
                <Highlights loading={loading} highlights={items} />
            </Box>
        </Box>
    );
};
export default ReadMore;
